/**
 * Utility functions for building hierarchical classification structures
 */

/**
 * Build a full path (breadcrumb) for a classification by traversing parent_id
 * Stops when a parent is not found in the categories array (missing/inaccessible parent)
 * @param {number} categoryId - The ID of the category
 * @param {Array} categories - Flat array of all categories with id, name, and parent_id
 * @returns {Array} Array of category objects from root to the specified category
 */
export const getClassificationPath = (categoryId, categories) => {
  if (!categoryId || !categories || categories.length === 0) {
    return [];
  }

  const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
  const path = [];
  let currentId = categoryId;
  const visited = new Set(); // Prevent infinite loops

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const category = categoryMap.get(currentId);
    
    // If category doesn't exist, stop building path
    if (!category) break;
    
    path.unshift(category);
    
    // If parent doesn't exist in the map, stop (parent is inaccessible)
    if (!category.parent_id || !categoryMap.has(category.parent_id)) {
      break;
    }
    
    currentId = category.parent_id;
  }

  return path;
};

/**
 * Build a hierarchical label (breadcrumb style) for a classification
 * @param {number} categoryId - The ID of the category
 * @param {Array} categories - Flat array of all categories
 * @returns {string} Hierarchical label like "Parent > Child > Grandchild"
 */
export const getClassificationHierarchicalLabel = (categoryId, categories) => {
  const path = getClassificationPath(categoryId, categories);
  return path.map(cat => cat.name).join(' > ');
};

/**
 * Calculate the depth level of a category in the hierarchy
 * Only counts parents that exist in the categories array
 * @param {Object} category - Category object with id and parent_id
 * @param {Array} categories - Flat array of all categories
 * @returns {number} Depth level (0 for root, 1 for first child, etc.)
 */
const getCategoryDepth = (category, categories) => {
  if (!category.parent_id) {
    return 0;
  }
  
  const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
  let depth = 0;
  let currentId = category.parent_id;
  const visited = new Set();
  
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const parent = categoryMap.get(currentId);
    // If parent doesn't exist in the array, stop counting depth
    if (!parent) break;
    depth++;
    if (!parent.parent_id) break;
    currentId = parent.parent_id;
  }
  
  return depth;
};

/**
 * Build hierarchical options for react-select from flat category list with tree-style formatting
 * @param {Array} categories - Flat array of categories with id, name, and parent_id
 * @param {Object} options - Configuration options
 * @param {number|null} options.excludeId - Category ID to exclude (e.g., when editing)
 * @param {boolean} options.showHierarchy - Whether to show hierarchical labels (default: true)
 * @param {string} options.separator - Separator for hierarchy (default: ' > ') - used for breadcrumb style
 * @param {boolean} options.treeStyle - Whether to use tree-style formatting with indentation (default: true)
 * @returns {Array} Array of options formatted for react-select
 */
export const buildHierarchicalClassificationOptions = (categories, options = {}) => {
  const {
    excludeId = null,
    showHierarchy = true,
    separator = ' > ',
    treeStyle = true
  } = options;

  if (!categories || categories.length === 0) {
    return [];
  }

  // Filter out excluded category
  let filteredCategories = categories;
  if (excludeId) {
    filteredCategories = categories.filter(cat => cat.id !== excludeId);
  }

  // Create a map for quick parent lookup
  const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
  
  // Helper to check if parent exists and is accessible
  const hasAccessibleParent = (category) => {
    if (!category.parent_id) {
      return false;
    }
    return categoryMap.has(category.parent_id);
  };

  // Build options with hierarchical labels
  return filteredCategories.map(category => {
    let label = category.name;
    
    if (showHierarchy) {
      // Only show hierarchy if parent exists in the categories array
      const hasParent = hasAccessibleParent(category);
      
      if (treeStyle && hasParent) {
        // Tree-style formatting with indentation and dashes
        // Using non-breaking spaces to ensure indentation is preserved
        const depth = getCategoryDepth(category, categories);
        const indent = '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'.repeat(depth); // 4 non-breaking spaces per level
        label = `${indent} -- \u00A0 ${category.name}`;
      } else if (!treeStyle && hasParent) {
        // Breadcrumb-style formatting
        const path = getClassificationPath(category.id, categories);
        if (path.length > 1) {
          label = path.map(cat => cat.name).join(separator);
        }
      }
      // If parent doesn't exist, label remains as category.name (no hierarchy)
    }

    return {
      value: category.id,
      label: label, // Hierarchical label for dropdown display
      originalLabel: category.name, // Original name without indentation for selected value
      data: category
    };
  });
};

/**
 * Sort categories hierarchically (parents before children) with favorites first at each level
 * Handles missing parents by treating categories with inaccessible parents as root-level
 * @param {Array} categories - Flat array of categories
 * @returns {Array} Sorted array with parents before their children, favorites first at each level
 */
export const sortCategoriesHierarchically = (categories) => {
  if (!categories || categories.length === 0) {
    return [];
  }

  const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
  const sorted = [];
  const added = new Set();

  // Helper function to get effective parent_id (handles missing parents)
  const getEffectiveParentId = (category) => {
    if (!category.parent_id) {
      return null;
    }
    
    // Check if parent exists in the categories array
    if (categoryMap.has(category.parent_id)) {
      return category.parent_id;
    }
    
    // Parent doesn't exist, so treat as root-level
    return null;
  };

  // Helper function to sort categories by parent_id, with favorites first
  const sortByParent = (parentId) => {
    const children = categories.filter(cat => {
      const effectiveParentId = getEffectiveParentId(cat);
      if (parentId === null) {
        return effectiveParentId === null;
      }
      return effectiveParentId === parentId;
    });

    // Sort: favorites first, then by name
    children.sort((a, b) => {
      const aFavorite = a.is_favorite ? 1 : 0;
      const bFavorite = b.is_favorite ? 1 : 0;
      if (bFavorite !== aFavorite) {
        return bFavorite - aFavorite; // Favorites first
      }
      return (a.name || '').localeCompare(b.name || '');
    });

    return children;
  };

  // Recursive function to add categories hierarchically
  const addCategoriesRecursively = (parentId) => {
    const children = sortByParent(parentId);
    
    children.forEach(category => {
      if (!added.has(category.id)) {
        sorted.push(category);
        added.add(category.id);
        // Recursively add children
        addCategoriesRecursively(category.id);
      }
    });
  };

  // Start with root level (parent_id is null or parent doesn't exist)
  addCategoriesRecursively(null);

  return sorted;
};

